import { ScreenManager } from "../../managers/ScreenManager";
import { ShowableEvents } from "../../mixins/Showable";
import { IconButton } from "../../ui/IconButton";
import { RectSize } from "../../utils/RectSize";
import { LoaderFileKeys } from "../constant/AppConstants";
import { AppSettings } from "../managers/AppSettings";
import { ScrollableContentScreenBase } from "./ScrollableContentScreenBase";
import { OptionsCategoryContent } from "./options/OptionsCategoryContent";
import { OptionsContentScroll } from "./options/OptionsContentScroll";
import { OptionsLeafPopup } from "./options/OptionsLeafPopup";
import { LayoutDataManager } from "../managers/LayoutDataManager";
import { VariationId } from "./TableScreen";
import { AppLocalization, LanguageId } from "../localization/AppLocalization";
import { ButtonEvents } from "../../ui/ButtonBase";
import { LocalizationEvents } from "../localization/Localization";
import { PopupBinary } from "../popup/PopupBinary";
import { ScreenOrientationType } from "../../utils/GetOrientation";
import { GTMTracking } from "../managers/GTMTracking";
import { ApplyScalingBias } from "../../utils/ApplyScalingBias";


export class OptionsScreen
    extends ScrollableContentScreenBase {
    declare contentsScrollPane: OptionsContentScroll;
    public btnClose: IconButton;
    public leafItemPopup: OptionsLeafPopup;
    public binaryPopup: PopupBinary;

    protected onCloseClick: Function;
    // following 2 vars reflect current component state
    protected curVariationId: VariationId;
    protected curLanguageId: LanguageId;
    /**
     * 
     */
    protected isDirty: boolean;


    public override Init(
        onCloseClick?: Function,
        variationId: VariationId = undefined,
        languageId: LanguageId = undefined,
    ): void {
        // > ApplyMixins (Showable) + SetTweenProps
        super.Init();

        this.onCloseClick = onCloseClick;
        this.curVariationId = variationId;
        this.curLanguageId = languageId;
    }

    public override Build(): void {
        // build subclass objects required by base
        // custom ContentsScrollPane
        // NOTE: add in base
        this.contentsScrollPane = new OptionsContentScroll(this.scene);
        this.contentsScrollPane.Init(
            true,
            LayoutDataManager.GetSharedData().general.scrollableContent.size.innerScale, // referenceWidth
            // this.curVariationId,
            // this.curLanguageId
        );
        this.contentsScrollPane.Build();

        // build base
        // > bg
        // > (add) contentsScrollPane
        // > btnPrev
        // > btnNext
        // > title
        super.Build();

        // btnClose
        this.btnClose = new IconButton(this.scene);
        this.btnClose.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-over.png" },
            disabled: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-disabled.png" },
        });
        this.btnClose.InitIcon({
            out: { texture: LoaderFileKeys.ui, frame: "icon-x-black.png" }
        });
        this.btnClose.PostInit(50);
        this.add(this.btnClose);

        // leafItemPopup
        this.leafItemPopup = new OptionsLeafPopup(this.scene);
        this.leafItemPopup.Init(
            // this.OnLeafItemPopupShowStart.bind(this), 
            // this.OnLeafItemPopupHideComplete.bind(this)
        );
        this.leafItemPopup.Build();
        this.leafItemPopup.Hide(false);
        // this.leafItemPopup.Show();
        this.add(this.leafItemPopup);
        // AFTER ScrollPane:Hide, perma-listen to onHidecomplete
        // perma listener: not enabled/disabled via this.UnregisterListeners
        this.leafItemPopup.GetEventEmitter().on(ShowableEvents.SHOW_START, this.OnLeafItemPopupShowStart, this);
        this.leafItemPopup.GetEventEmitter().on(ShowableEvents.HIDE_COMPLETE, this.OnLeafItemPopupHideComplete, this);

        // set ref leafItemPopup to each content
        this.contentsScrollPane.GetContents().forEach((content: OptionsCategoryContent) => {
            content.SetLeafItemPopup(this.leafItemPopup);
        });

        // Reset Options / Statistics
        this.binaryPopup = new PopupBinary(this.scene);
        this.binaryPopup.Init(true);
        this.binaryPopup.Build();
        this.binaryPopup.Hide(false);
        this.add(this.binaryPopup);

        // > contentsScrollPane
        // > title (contentsScrollData->capitalizedTitle)
        this.UpdateFromLocalizationChange();

        // listen to language change
        AppLocalization.GetEmitter().on(LocalizationEvents.LANGUAGE_CHANGED, this.OnLanguageChange, this);
    }

    protected OnShowStart(animated: boolean): void {
        GTMTracking.PLDTrackingMenu("options_"+VariationId[this.curVariationId]);
        super.OnShowStart(animated);
    }

    private OnLanguageChange(languageId: LanguageId): void {
        this.SetData(this.curVariationId, languageId);
        // runs ContentScrollPane#Size, that updates this.contentsScrollData[i].scrollY (so title can scroll contents)
        this.OnResizeManual();
    }

    public SetData(variationId: VariationId = undefined, languageId: LanguageId = undefined): void {
        // console.log(`SetData\n${VariationId[this.curVariationId]}->${VariationId[variationId]}\n${LanguageId[this.curLanguageId]}->${LanguageId[languageId]}`);

        let languageOrVariationChange: boolean = false;
        if (variationId != undefined && this.curVariationId != variationId) {
            this.curVariationId = variationId;
            languageOrVariationChange = true;
        }
        if (languageId != undefined && this.curLanguageId != languageId) {
            this.curLanguageId = languageId;
            languageOrVariationChange = true;
        }

        // if language or variation changed
        if (languageOrVariationChange || this.isDirty) {
            // rebuild all contents
            (this.contentsScrollPane as OptionsContentScroll).SetData(
                this.curVariationId,
                this.curLanguageId,
            );
            // update scroll data
            this.UpdateFromLocalizationChange();
            this.isDirty = false;
        }
        // console.log("isDirty?", isDirty);
    }

    public override UpdateFromLocalizationChange(): void {
        // console.log("UpdateFromLocalizationChange",VariationId[this.curVariationId], LanguageId[this.curLanguageId]);

        // update section title data for title on this level
        this.contentsScrollPane.UpdateFromLocalizationChange();
        // update view from the data
        this.title.text = this.contentsScrollPane.GetCurrentContentScrollData().capitalizedTitle;
        // console.log(this.title.text);
    }

    /**
     * Sometimes it might be needed to set dirty from outisde 
     * (when live data model changes externally, but not languageId or variationId)
     * 
     * @param isDirty 
     */
    public SetDirty(isDirty: boolean): void {
        this.isDirty = isDirty;
    }

    protected OnLeafItemPopupShowStart(): void {
        // console.log("OnLeafItemPopupShowStart")
        this.contentsScrollPane.UnregisterListeners();
    }

    protected OnLeafItemPopupHideComplete(): void {
        // console.log("OnLeafItemPopupHideComplete")
        this.contentsScrollPane.RegisterListeners();
    }

    protected override RegisterListeners(): void {
        // > btnPrev
        // > btnNext
        // > contentsScrollPane
        // > ScreenManagerEvents.RESIZE
        super.RegisterListeners();

        this.btnClose.on(ButtonEvents.CLICK, this.onCloseClick, this);
    }

    protected override UnregisterListeners(): void {
        super.UnregisterListeners();

        this.btnClose.off(ButtonEvents.CLICK, this.onCloseClick, this);
    }

    protected override OnResize(windowRect: RectSize): void {
        // console.log("OnResize");

        super.OnResize(windowRect);

        const maxWidth = LayoutDataManager.GetSharedData().general.scrollableContent.size.maxWidth;
        const popupScale: number = ApplyScalingBias(1.2 * this.scrollPaneWidth / maxWidth, 1.0);
        const screenCenterX: number = (0.5 * windowRect.width) | 0;
        const screenCenterY: number = (0.5 * windowRect.height) | 0;
        const screenOrientationType: ScreenOrientationType = ScreenManager.GetOrientation();

        const btnCloseScale = ScreenManager.GetAssetScale(
            LayoutDataManager.GetSharedData().general.screen.closeButton.baseScale,
            LayoutDataManager.GetSharedData().general.screen.closeButton.scaleBias,
        );
        this.btnClose.setScale(btnCloseScale);
        this.btnClose.setPosition(windowRect.width * 1.0 - 90 * btnCloseScale, 90 * btnCloseScale);

        if (this.leafItemPopup.IsShowing()) {
            this.leafItemPopup.OnResize(windowRect);
        }


        // let popupScale: number;
        let popupY: number;
        switch (screenOrientationType) {
            case ScreenOrientationType.LANDSCAPE:
                popupY = screenCenterY;
                break;
            case ScreenOrientationType.PORTRAIT:
                popupY = screenCenterY;
                break;
        }
        this.binaryPopup.scale = popupScale;
        this.binaryPopup.setPosition(screenCenterX, popupY);
        if (this.binaryPopup.IsShowing()) {
            this.binaryPopup.OnResize(windowRect);
        }
    }
}